import type { RoomRateSelection } from '../surcharges/surchargeUtils';

import { type EgsRoomMatch, getEgsMatchScope } from './egsRoomMatcher';
import type { EgsGuestSlot, EgsRoomGroup } from './types';

export interface EgsApplyCandidate {
  /** The room rates the CM may tick for this record. Empty means the record cannot be applied at all. */
  candidates: Array<RoomRateSelection>;
  defaultSelectedRoomRateIds: Array<string>;
  /** The board basis the record named, where it named one. Shown on the pick, never used to pick. */
  ratePlanQualifier?: string;
  /** True when a human has to choose before anything can be written. */
  requiresPick: boolean;
  roomLabel: string;
  /** The live guest types this surcharge prices, so the mapping step can say what is at stake. */
  slots: Array<EgsGuestSlot>;
}

function selectionsForRoomTypes(
  roomRateSelections: Array<RoomRateSelection>,
  roomTypeIds: Set<string>,
): Array<RoomRateSelection> {
  return roomRateSelections.filter((selection) => roomTypeIds.has(String(selection.roomType.id)));
}

function toRoomRateIds(selections: Array<RoomRateSelection>): Array<string> {
  return selections.map((selection) => String(selection.roomRate.id));
}

function countRoomTypes(selections: Array<RoomRateSelection>): number {
  return new Set(selections.map((selection) => String(selection.roomType.id))).size;
}

/**
 * The rate plan is applied before this runs, so an ambiguous name can arrive with only one of its room types
 * still selling, and asking a CM to pick from one row is a question with one answer. A `confirm` is never
 * collapsed that way, however few rooms are left.
 */
export function buildEgsApplyCandidate({
  match,
  roomLabel,
  roomRateSelections,
  slots = [],
}: {
  match: EgsRoomMatch;
  roomLabel: string;
  roomRateSelections: Array<RoomRateSelection>;
  slots?: Array<EgsGuestSlot>;
}): EgsApplyCandidate {
  const { pickReason, ratePlanQualifier, roomTypeIds } = getEgsMatchScope(match);
  const candidates =
    roomTypeIds === 'every' ? roomRateSelections : selectionsForRoomTypes(roomRateSelections, roomTypeIds);
  const requiresPick = pickReason === 'confirm' || (pickReason === 'ambiguous' && countRoomTypes(candidates) > 1);

  return {
    candidates,
    defaultSelectedRoomRateIds: requiresPick ? [] : toRoomRateIds(candidates),
    ratePlanQualifier,
    requiresPick,
    roomLabel,
    slots,
  };
}

/** `candidates` only comes back empty where the verdict narrowed to nothing; filter with `canApplyEgsCandidate`. */
export function buildEgsApplyCandidates({
  matchByRoomLabel,
  roomGroups,
  roomRateSelections,
}: {
  matchByRoomLabel: Record<string, EgsRoomMatch>;
  roomGroups: Array<EgsRoomGroup>;
  roomRateSelections: Array<RoomRateSelection>;
}): Array<EgsApplyCandidate> {
  return roomGroups
    .filter((group) => group.slots.length > 0)
    .map((group) =>
      buildEgsApplyCandidate({
        match: matchByRoomLabel[group.roomLabel] ?? { source: 'unmatched' },
        roomLabel: group.roomLabel,
        roomRateSelections,
        slots: group.slots,
      }),
    );
}

export function canApplyEgsCandidate(candidate: EgsApplyCandidate) {
  return candidate.candidates.length > 0;
}

export function buildDefaultSelectedRoomRateIds(candidates: Array<EgsApplyCandidate>): Record<string, Array<string>> {
  return Object.fromEntries(candidates.map((candidate) => [candidate.roomLabel, candidate.defaultSelectedRoomRateIds]));
}
